import { ReactNode, useCallback, useState } from 'react';
import { createContext } from 'react';

import { CurrencyTypeEnum } from '../model/currency_type.enum';
import { DataLabelEnum } from '../model/data_label.enum';
import { RangeEnum } from '../model/range.enum';
import Utils from '../utils/immersive_utils';

import useLayout, { KEYS } from './layout-hook';
import usePersistentState from './use-persistent-state';

const LAYOUT_KEY = 'SECTION_GALLERY_LAYOUT';
const DEFAULT_LAYOUTS = {
  [KEYS.LG]: [
    { i: 'vol-smile', x: 0, y: 0, w: 6, h: 4 },
    { i: 'vol-smile-compare', x: 6, y: 0, w: 6, h: 4 },
    { i: 'term-structure', x: 0, y: 4, w: 6, h: 4 },
    { i: 'term-structure-compare', x: 6, y: 4, w: 6, h: 4 },
    { i: 'model-volatility-surface', x: 0, y: 4, w: 6, h: 4 },
    { i: 'scatter-plot', x: 6, y: 4, w: 6, h: 4 },
    { i: 'volatility-cones', x: 0, y: 4, w: 6, h: 4 },
    { i: 'volatility-heatmap', x: 6, y: 8, w: 6, h: 4 },
    { i: 'footprint-volatility', x: 0, y: 8, w: 6, h: 4 },
    { i: 'open-interest', x: 6, y: 8, w: 6, h: 4 },
  ],
  [KEYS.MD]: [
    { i: 'vol-smile', x: 0, y: 0, w: 6, h: 4 },
    { i: 'vol-smile-compare', x: 8, y: 0, w: 6, h: 4 },
    { i: 'term-structure', x: 0, y: 4, w: 6, h: 4 },
    { i: 'term-structure-compare', x: 6, y: 4, w: 6, h: 4 },
    { i: 'model-volatility-surface', x: 0, y: 8, w: 6, h: 4 },
    { i: 'scatter-plot', x: 6, y: 8, w: 6, h: 4 },
    { i: 'volatility-cones', x: 0, y: 8, w: 6, h: 4 },
    { i: 'volatility-heatmap', x: 6, y: 12, w: 6, h: 4 },
    { i: 'footprint-volatility', x: 0, y: 12, w: 6, h: 4 },
    { i: 'open-interest', x: 6, y: 12, w: 6, h: 4 },
  ],
  [KEYS.SM]: [
    { i: 'vol-smile', x: 0, y: 0, w: 12, h: 4 },
    { i: 'term-structure', x: 8, y: 0, w: 12, h: 4 },
    { i: 'vol-smile-compare', x: 8, y: 0, w: 12, h: 4 },
    { i: 'term-structure-compare', x: 0, y: 0, w: 12, h: 4 },
    { i: 'model-volatility-surface', x: 8, y: 0, w: 12, h: 4 },
    { i: 'scatter-plot', x: 12, y: 0, w: 12, h: 4 },
    { i: 'volatility-cones', x: 8, y: 0, w: 12, h: 4 },
    { i: 'volatility-heatmap', x: 6, y: 4, w: 6, h: 4 },
    { i: 'footprint-volatility', x: 0, y: 4, w: 6, h: 4 },
    { i: 'open-interest', x: 6, y: 4, w: 6, h: 4 },
  ],
  [KEYS.XS]: [
    { i: 'vol-smile', x: 0, y: 0, w: 12, h: 4 },
    { i: 'vol-smile-compare', x: 8, y: 0, w: 12, h: 4 },
    { i: 'term-structure', x: 0, y: 0, w: 12, h: 4 },
    { i: 'term-structure-compare', x: 0, y: 0, w: 12, h: 4 },
    { i: 'model-volatility-surface', x: 8, y: 0, w: 12, h: 4 },
    { i: 'scatter-plot', x: 8, y: 0, w: 12, h: 4 },
    { i: 'volatility-cones', x: 8, y: 0, w: 12, h: 4 },
    { i: 'volatility-heatmap', x: 6, y: 4, w: 6, h: 4 },
    { i: 'footprint-volatility', x: 0, y: 4, w: 6, h: 4 },
    { i: 'open-interest', x: 6, y: 4, w: 6, h: 4 },
  ],
  [KEYS.XXS]: [
    { i: 'vol-smile', x: 0, y: 0, w: 12, h: 4 },
    { i: 'vol-smile-compare', x: 8, y: 0, w: 12, h: 4 },
    { i: 'term-structure', x: 0, y: 0, w: 12, h: 4 },
    { i: 'term-structure-compare', x: 0, y: 0, w: 12, h: 4 },
    { i: 'model-volatility-surface', x: 8, y: 0, w: 12, h: 4 },
    { i: 'scatter-plot', x: 8, y: 0, w: 12, h: 4 },
    { i: 'volatility-cones', x: 8, y: 0, w: 12, h: 4 },
    { i: 'volatility-heatmap', x: 6, y: 4, w: 6, h: 4 },
    { i: 'footprint-volatility', x: 0, y: 4, w: 6, h: 4 },
    { i: 'open-interest', x: 6, y: 4, w: 6, h: 4 },
  ],
};

interface IGalleryParamsProvider {
  children?: ReactNode;
}

interface IGalleryProvider {
  layouts: any;
  maximizeCard: (card: string) => void;
  setLayouts: (props: any) => void;
  maximized: string;
  setMaximized: (props: string) => void;
  resetLayout: () => void;
  changeLayoutHeight: (card: string, values: { size: string; value: number }[]) => void;
  range: RangeEnum;
  setRange: (props: RangeEnum) => void;
  currency: CurrencyTypeEnum;
  formatCurrency: (token: CurrencyTypeEnum) => string;
  setCurrency: (props: CurrencyTypeEnum) => void;
  label: DataLabelEnum;
  setLabel: (props: DataLabelEnum) => void;
  rowExpanded: Set<string>;
  toggleRowExpand: (card: string) => void;
}

export const GalleryContext = createContext({} as IGalleryProvider);

export const GalleryProvider = (props: IGalleryParamsProvider) => {
  const [currency, setCurrency] = usePersistentState<CurrencyTypeEnum>(
    ['gallery'],
    'currency',
    CurrencyTypeEnum.BITCOIN,
  );
  const [label, setLabel] = useState<DataLabelEnum>(DataLabelEnum.PERPETUAL);
  const [range, setRange] = useState<RangeEnum>(RangeEnum.D1);

  const formatCurrency = Utils.formatCurrency;

  const { setLayouts, layouts, maximized, setMaximized, changeLayoutHeight } = useLayout(
    LAYOUT_KEY,
    DEFAULT_LAYOUTS,
  );

  const [rowExpanded, setRowExpanded] = useState<Set<string>>(new Set());
  const [baselineLayouts, setBaselineLayouts] = useState<any | null>(null);

  const toggleRowExpand = (card: string) => {
    const next = new Set(rowExpanded);
    if (next.has(card)) {
      next.delete(card);
    } else {
      next.add(card);
    }

    if (rowExpanded.size === 0 && next.size > 0 && !baselineLayouts) {
      setBaselineLayouts(layouts);
    }

    if (next.size === 0) {
      if (baselineLayouts) setLayouts(baselineLayouts);
      setBaselineLayouts(null);
      setRowExpanded(next);
      return;
    }

    const COLS_BY_KEY: Record<string, number> = { lg: 12, md: 12, sm: 12, xs: 1, xxs: 1 };
    const source = baselineLayouts || layouts;
    const recomputed: any = JSON.parse(JSON.stringify(source));

    Object.keys(recomputed).forEach((bp) => {
      const cols = COLS_BY_KEY[bp] ?? COLS_BY_KEY.lg;
      recomputed[bp] = recomputed[bp].map((item: any) => {
        if (next.has(item.i)) {
          return { ...item, x: 0, w: cols, static: true };
        }
        return { ...item, static: false };
      });
    });

    setRowExpanded(next);
    setLayouts(recomputed);
  };

  const resetGalleryLayout = useCallback(() => {
    setRowExpanded(new Set());
    setBaselineLayouts(null);
    setMaximized('');
    setLayouts(DEFAULT_LAYOUTS);
  }, [setLayouts, setMaximized]);

  return (
    <GalleryContext.Provider
      value={{
        setLayouts,
        layouts,
        maximizeCard: toggleRowExpand,
        maximized,
        setMaximized,
        resetLayout: resetGalleryLayout,
        changeLayoutHeight,
        range,
        setRange,
        currency,
        setCurrency,
        formatCurrency,
        label,
        setLabel,
        rowExpanded,
        toggleRowExpand,
      }}
    >
      {props.children}
    </GalleryContext.Provider>
  );
};
